import React from 'react'

import { Banner, Button } from 'slab'

type OnboardingStatus =
  | 'not_started'
  | 'pending'
  | 'in_progress'
  | 'complete'
  | 'action_required'

type Props = {
  merchantName: string
  stripeOnboardingStatus: OnboardingStatus
  hasStripeAccount: boolean
  formAction: string
  authenticityToken: string
}

const statusCopy: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  action_required: 'Action Required',
  complete: 'Complete',
  not_started: 'Not Started',
}

const friendlyStatus = (status: OnboardingStatus) => {
  if (!status) {
    return statusCopy.not_started
  }

  return statusCopy[status] ?? status.replace(/_/g, ' ')
}

const MerchantAdminOnboarding: React.FC<Props> = ({
  merchantName,
  stripeOnboardingStatus,
  hasStripeAccount,
  formAction,
  authenticityToken,
}) => {
  const isComplete = hasStripeAccount && stripeOnboardingStatus === 'complete'
  const shouldShowStatusBanner = hasStripeAccount && !isComplete

  return (
    <div className='min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-lg mx-auto'>
        <section className='bg-white border border-gray-100 rounded-xl shadow-md p-6 space-y-6'>
          <header className='space-y-2 text-center'>
            <h1 className='text-3xl font-semibold text-gray-900'>
              Welcome{merchantName ? `, ${merchantName}` : ''}!
            </h1>
            <p className='body text-gray-600'>Please complete your onboarding with Stripe</p>
          </header>

          {hasStripeAccount
            ? (
              <div className='space-y-4'>
                {isComplete
                  ? (
                    <Banner type='success' title='Onboarding complete!'>
                      Your Stripe account is fully set up.
                    </Banner>
                    )
                  : null}

                {shouldShowStatusBanner
                  ? (
                    <Banner
                      type='warning'
                      title={<span>Onboarding status: <span className='font-semibold'>{friendlyStatus(stripeOnboardingStatus)}</span></span>}
                    >
                      Continue your Stripe onboarding to complete setup.
                    </Banner>
                    )
                  : null}

                {!isComplete
                  ? (
                    <form action={formAction} method='post' data-turbo='false' className='space-y-4'>
                      <input type='hidden' name='authenticity_token' value={authenticityToken} />
                      <Button type='submit' variant='primary' width='full'>
                        Continue onboarding
                      </Button>
                    </form>
                    )
                  : null}
              </div>
              )
            : (
              <form action={formAction} method='post' data-turbo='false' className='space-y-4'>
                <input type='hidden' name='authenticity_token' value={authenticityToken} />
                <Button type='submit' variant='primary' width='full'>
                  Start Stripe onboarding
                </Button>
              </form>
              )}
        </section>
      </div>
    </div>
  )
}

export default MerchantAdminOnboarding
